import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

const statusLabels = { draft: 'Taslak', approved: 'Onaylandi' };
const statusColors = { draft: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]' };
const months = ['Ocak','Subat','Mart','Nisan','Mayis','Haziran','Temmuz','Agustos','Eylul','Ekim','Kasim','Aralik'];

export default function PayrollList() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const now = new Date();
  const [form, setForm] = useState({
    employee_id: '', period_year: String(now.getFullYear()), period_month: String(now.getMonth() + 1),
    net_salary: '', overtime_pay: '0', bonus: '0',
    meal_allowance: '0', transport_allowance: '0', phone_allowance: '0',
    insurance_deduction: '0', tax_deduction: '0', advance_deduction: '0', other_deductions: '0',
    currency: 'TRY'
  });
  const [loading, setLoading] = useState(false);

  const fetch = () => api.get('/hr/payroll').then(r => setRecords(r.data)).catch(() => {});
  useEffect(() => { fetch(); api.get('/hr/employees').then(r => setEmployees(r.data)).catch(() => {}); }, []);

  // Auto-fill salary when employee selected
  const selectEmployee = async (empId) => {
    setForm(p => ({ ...p, employee_id: empId }));
    try {
      const res = await api.get(`/hr/employees/${empId}/salary`);
      if (res.data.length > 0) {
        const s = res.data[0];
        setForm(p => ({ ...p, net_salary: String(s.net_salary || ''), meal_allowance: String(s.meal_allowance || 0),
          transport_allowance: String(s.transport_allowance || 0), phone_allowance: String(s.phone_allowance || 0) }));
      }
    } catch {}
  };

  const handleCreate = async () => {
    setLoading(true);
    try { await api.post('/hr/payroll', form); toast.success('Bordro olusturuldu'); setShowNew(false); fetch(); }
    catch (err) { toast.error('Hata'); }
    setLoading(false);
  };

  const approve = async (id) => { await api.patch(`/hr/payroll/${id}/approve`); toast.success('Onaylandi'); fetch(); };

  const totalNet = parseFloat(form.net_salary || 0) + parseFloat(form.overtime_pay || 0) + parseFloat(form.bonus || 0) +
    parseFloat(form.meal_allowance || 0) + parseFloat(form.transport_allowance || 0) + parseFloat(form.phone_allowance || 0) -
    parseFloat(form.insurance_deduction || 0) - parseFloat(form.tax_deduction || 0) -
    parseFloat(form.advance_deduction || 0) - parseFloat(form.other_deductions || 0);

  return (
    <div data-testid="payroll-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Bordro</h1>
        <Button onClick={() => setShowNew(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"><Plus size={16} className="mr-2" /> Yeni Bordro</Button>
      </div>
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Personel</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Donem</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Net Maas</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Toplam Net</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islem</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.map(r => (
              <TableRow key={r.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell className="font-medium text-[#1a1916]">{r.employee_name}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{months[(r.period_month || 1) - 1]} {r.period_year}</TableCell>
                <TableCell className="text-right font-mono text-sm text-[#3F3E39]">{r.net_salary?.toFixed(2)} {r.currency}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{r.total_net?.toFixed(2)} {r.currency}</TableCell>
                <TableCell><span className={`status-badge ${statusColors[r.status]}`}>{statusLabels[r.status]}</span></TableCell>
                <TableCell className="text-right">
                  {r.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => approve(r.id)} className="text-[#3d6e4e] h-7"><Check size={14} className="mr-1" /> Onayla</Button>}
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Kayit yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Bordro</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Personel *</Label>
              <Select value={form.employee_id || undefined} onValueChange={selectEmployee}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue placeholder="Sec" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Yil</Label><Input value={form.period_year} onChange={(e) => setForm(p => ({ ...p, period_year: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label>Ay</Label>
                <Select value={form.period_month} onValueChange={(v) => setForm(p => ({ ...p, period_month: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <p className="text-xs font-bold uppercase text-[#8a7e70]">Gelirler</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Net Maas</Label><Input type="number" step="0.01" value={form.net_salary} onChange={(e) => setForm(p => ({ ...p, net_salary: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Fazla Mesai</Label><Input type="number" step="0.01" value={form.overtime_pay} onChange={(e) => setForm(p => ({ ...p, overtime_pay: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Prim</Label><Input type="number" step="0.01" value={form.bonus} onChange={(e) => setForm(p => ({ ...p, bonus: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Yemek</Label><Input type="number" step="0.01" value={form.meal_allowance} onChange={(e) => setForm(p => ({ ...p, meal_allowance: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Ulasim</Label><Input type="number" step="0.01" value={form.transport_allowance} onChange={(e) => setForm(p => ({ ...p, transport_allowance: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Telefon</Label><Input type="number" step="0.01" value={form.phone_allowance} onChange={(e) => setForm(p => ({ ...p, phone_allowance: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
            </div>
            <p className="text-xs font-bold uppercase text-[#8a7e70]">Kesintiler</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">SGK</Label><Input type="number" step="0.01" value={form.insurance_deduction} onChange={(e) => setForm(p => ({ ...p, insurance_deduction: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Vergi</Label><Input type="number" step="0.01" value={form.tax_deduction} onChange={(e) => setForm(p => ({ ...p, tax_deduction: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Avans</Label><Input type="number" step="0.01" value={form.advance_deduction} onChange={(e) => setForm(p => ({ ...p, advance_deduction: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Diger</Label><Input type="number" step="0.01" value={form.other_deductions} onChange={(e) => setForm(p => ({ ...p, other_deductions: e.target.value }))} className="bg-white border-[#d0c4b0] h-8" /></div>
            </div>
            <div className="p-3 bg-[#e8dfd0]/50 rounded-lg text-center">
              <p className="text-xs text-[#8a7e70]">Toplam Net Odeme</p>
              <p className="text-2xl font-bold text-[#1a1916]">{totalNet.toFixed(2)} {form.currency}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
